import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { UpdateVestingTermDto, VestingTermRes } from '@dealstack/shared/lib/generated';
import { useCallback, useMemo } from 'react';
import { z } from 'zod';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

export type ValidationResult = { error: string | null };

const getTimeBasedPercentageShape = (tCommonUi: TranslateFunction<I18Namespaces.COMMON_UI>) =>
	z
		.number({
			invalid_type_error: tCommonUi('vesting_terms.error.time_based.number'),
			required_error: tCommonUi('vesting_terms.error.time_based.required'),
		})
		.nonnegative(tCommonUi('vesting_terms.error.time_based.nonnegative'))
		.max(100, tCommonUi('vesting_terms.error.time_based.max'));

const getPerformancePercentageShape = (tCommonUi: TranslateFunction<I18Namespaces.COMMON_UI>) =>
	z
		.number({
			invalid_type_error: tCommonUi('vesting_terms.error.performance.number'),
			required_error: tCommonUi('vesting_terms.error.performance.required'),
		})
		.nonnegative(tCommonUi('vesting_terms.error.performance.nonnegative'))
		.max(100, tCommonUi('vesting_terms.error.performance.max'));

const getOptionPeriodShape = (tCommonUi: TranslateFunction<I18Namespaces.COMMON_UI>) =>
	z
		.number({
			invalid_type_error: tCommonUi('vesting_terms.error.option_period.number'),
			required_error: tCommonUi('vesting_terms.error.option_period.required'),
		})
		.int(tCommonUi('vesting_terms.error.option_period.int'))
		.positive(tCommonUi('vesting_terms.error.option_period.positive'));

export const useVestingTermValidators = (
	currentVestingTerm: VestingTermRes,
	onUpdateVestingTerm: (id: string, formValues: UpdateVestingTermDto) => void
) => {
	const tCommonUi = useTranslationCommonUi();

	const handleTimeBasedPercentageUpdate = useCallback(
		(id: string, value: number | string | null | undefined): ValidationResult => {
			const parsed = getTimeBasedPercentageShape(tCommonUi).safeParse(value);

			if (!parsed.success) {
				return { error: parsed.error.issues[0].message };
			}

			const maxAllowed = 100 - currentVestingTerm.performancePercentage;

			if (parsed.data > maxAllowed) {
				return {
					error: tCommonUi('vesting_terms.error.time_based.overflow', {
						max: maxAllowed,
						performance: currentVestingTerm.performancePercentage,
					}),
				};
			}

			onUpdateVestingTerm(id, { timeBasedPercentage: parsed.data });
			return { error: null };
		},
		[currentVestingTerm.performancePercentage, tCommonUi, onUpdateVestingTerm]
	);

	const handlePerformancePercentageUpdate = useCallback(
		(id: string, value: number | string | null | undefined): ValidationResult => {
			const parsed = getPerformancePercentageShape(tCommonUi).safeParse(value);
			if (!parsed.success) {
				return { error: parsed.error.issues[0].message };
			}

			const maxAllowed = 100 - currentVestingTerm.timeBasedPercentage;

			if (parsed.data > maxAllowed) {
				return {
					error: tCommonUi('vesting_terms.error.performance.overflow', {
						max: maxAllowed,
						timeBased: currentVestingTerm.timeBasedPercentage,
					}),
				};
			}

			onUpdateVestingTerm(id, { performancePercentage: parsed.data });
			return { error: null };
		},
		[currentVestingTerm.timeBasedPercentage, tCommonUi, onUpdateVestingTerm]
	);

	const handleOptionPeriodUpdate = useCallback(
		(id: string, value: number | string | null | undefined): ValidationResult => {
			const parsed = getOptionPeriodShape(tCommonUi).safeParse(value);

			if (!parsed.success) {
				return { error: parsed.error.issues[0].message };
			}

			onUpdateVestingTerm(id, { optionPeriod: parsed.data });
			return { error: null };
		},
		[tCommonUi, onUpdateVestingTerm]
	);

	return useMemo(
		() => ({
			handleTimeBasedPercentageUpdate,
			handlePerformancePercentageUpdate,
			handleOptionPeriodUpdate,
		}),
		[
			handleTimeBasedPercentageUpdate,
			handlePerformancePercentageUpdate,
			handleOptionPeriodUpdate,
		]
	);
};
